/**
 * Persist marketing landing `url_fragment` across auth redirects so authenticated Pros
 * can call POST /api/supplier_referral_connect/ after landing on /home (ticket 5).
 */

export const SUPPLIER_REFERRAL_URL_FRAGMENT_STORAGE_KEY = 'os_supplier_referral_url_fragment'

const REGISTER_RESERVED_FRAGMENTS = new Set(['legacy'])

function pathnameLooksLikeNearmapReferral(pathname: string): boolean {
  return pathname.toLowerCase().includes('nearmap')
}

/**
 * Parses /register-{fragment} and /login-{fragment} paths used for supplier (and partner) landing URLs.
 * Excludes known system routes (legacy register, nearmap, login-modal style fragments).
 */
export function extractSupplierReferralFragmentFromPath(pathname: string): string | null {
  if (!pathname || pathnameLooksLikeNearmapReferral(pathname)) {
    return null
  }

  if (pathname.startsWith('/register-')) {
    const fragment = pathname.slice('/register-'.length)
    if (!fragment || REGISTER_RESERVED_FRAGMENTS.has(fragment)) {
      return null
    }
    return fragment
  }

  if (pathname.startsWith('/login-')) {
    const fragment = pathname.slice('/login-'.length)
    if (!fragment) {
      return null
    }
    const lower = fragment.toLowerCase()
    if (lower === 'modal' || lower.startsWith('nearmap')) {
      return null
    }
    return fragment
  }

  return null
}

export function stashSupplierReferralUrlFragment(fragment: string | null | undefined): void {
  if (typeof window === 'undefined' || fragment == null) {
    return
  }
  const trimmed = String(fragment).trim()
  if (!trimmed || pathnameLooksLikeNearmapReferral(trimmed)) {
    return
  }
  try {
    window.sessionStorage.setItem(SUPPLIER_REFERRAL_URL_FRAGMENT_STORAGE_KEY, trimmed)
  } catch {
    // sessionStorage may be unavailable (private mode, SSR)
  }
}

export function stashSupplierReferralFragmentFromPath(pathname: string): void {
  const extracted = extractSupplierReferralFragmentFromPath(pathname)
  if (extracted) {
    stashSupplierReferralUrlFragment(extracted)
  }
}

/** Atomically read and remove the pending fragment so only one consumer processes it. */
export function claimSupplierReferralUrlFragment(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const value = window.sessionStorage.getItem(SUPPLIER_REFERRAL_URL_FRAGMENT_STORAGE_KEY)
    if (value) {
      window.sessionStorage.removeItem(SUPPLIER_REFERRAL_URL_FRAGMENT_STORAGE_KEY)
    }
    return value
  } catch {
    return null
  }
}
